import {useEffect, useMemo, useState} from 'react';
import {Grid, Snackbar, Stack} from '@mui/material';
import "./team.css";

import {ConfirmDialog, LabelledSingleSelect, SearchBar, StyledButtonContained, StyledButtonOutlined, useStyles} from "../../utils/uiUtils";
import {CreateEditDialog} from "../../component/CreateEditDialog/CreateEditDialog";
import {addUser, deleteUser, fetchCustomers, fetchUsersDetails, patchUser} from "../../utils/apiCalls";
import DataTable from "../../component/DataTable/DataTable";
import {renderUserActions, renderUserActiveStatus, renderUserAvatar} from "./userListRenderHelpers";
import {hasValue} from "../../utils/utils";
import {useAuth} from "../AuthProvider/AuthProvider";
import {variantUsersTableConfigMap} from "./userListTableConfig";
import {variantUsersDataProcessorFNMap} from "./variantUsersDataProcessor";
import {rolesList} from "./userListVariables";
import {dialogBoxConfig} from "../../component/CreateEditDialog/dialogBoxConfig";
import {describeInvitation} from "../../utils/invitationMessage";
import {apiErrorReason} from "../../utils/apiErrorReason";


/**
 * User List Component - Displays a list of users with options to add, edit, delete, deactivate/reactivate users.
 * @param variant - Variant of the user list. Supported:
 * - "CustomerUsers" - Variant used for customer view to manage their team members. The customer view will display the title "Team"
 * - "AllUsers" - Variant used for TapeArk admin view to manage all users.
 * @returns {Element}
 * @constructor
 */
export const UserList = ({variant}) => {
    const variantTitleMap = {
        "CustomerUsers": "My Team",
        "AllUsers": "All Users"
    };

    if (!Object.keys(variantTitleMap).includes(variant)) throw new Error(`Invalid variant ${variant} for UserList component`);

    const defaultRole = "creator";

    const classes = useStyles();
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [userCount, setUserCount] = useState(1);

    const [isToggleConfirmDialogOpen, setIsToggleConfirmDialogOpen] = useState(false);
    const [isCreateEditUserDialogOpen, setIsCreateEditUserDialogOpen] = useState(false);
    const [isDeleteConfirmDialogOpen, setIsDeleteConfirmDialogOpen] = useState(false);

    const [selectedUserData, setSelectedUserData] = useState({});
    const [rawUsersList, setRawUsersList] = useState([]);
    const [processedUsersList, setProcessedUsersList] = useState([]);
    const [customers, setCustomers] = useState(undefined);

    const [searchQuery, setSearchQuery] = useState("");
    const [roleFilter, setRoleFilter] = useState("all");
    const [companyFilter, setCompanyFilter] = useState("all");

    const {features, isAdmin: authIsAdmin, user} = useAuth()

    const [snackbarMessage, setSnackbarMessage] = useState("_");
    const [snackBarState, setSnackBarState] = useState({
        openSb: false,
        sbVertical: 'top',
        sbHorizontal: 'right',
    });
    const {sbVertical, sbHorizontal, openSb} = snackBarState;

    const [isAdmin, setIsAdmin] = useState(authIsAdmin);
    const [currentOperatorRole, setCurrentOperatorRole] = useState(user?.role ?? defaultRole);
    const [roles, setRoles] = useState([{label: "All", value: 'all'}]);

    const getLabelForRoleValue = (roleValue) => {
        const role = roles.find(r => r.value === roleValue);
        return role ? role.label : roleValue;
    };

    useEffect(() => {
        if (variant === "AllUsers") {
            setRoles([
                {label: "Super Admin", value: 'admin'},
                ...rolesList
            ]);
        } else setRoles(rolesList);
    }, [variant]);

    useEffect(() => {
        setIsAdmin(authIsAdmin);
        setCurrentOperatorRole(user?.role ?? defaultRole);
    }, [authIsAdmin, user]);

    const handlePageChange = (event, page) => {
        setCurrentPage(page);
    };

    const getUsersList = () => {
        fetchUsersDetails(currentPage, searchQuery, roleFilter, companyFilter, isAdmin, user?.customer_uuid).then(data => {
            setRawUsersList(data?.results ?? []);
            setUserCount(data?.count ?? 0);
            setTotalPages(data?.count ? Math.ceil(data.count / 10) : 0);
        }).catch(error => console.log("Error getting the users for current customer ", error));
    };

    const openSnackbar = (message) => {
        setSnackbarMessage(message)
        setSnackBarState({sbVertical: 'top', sbHorizontal: 'right', openSb: true});

        const sec = 1000;
        const snackbarCloseDelay = 10 * sec; // 3 seconds
        setTimeout(() => {
            closeSnackbar();
        }, snackbarCloseDelay);
    };

    const closeSnackbar = () => {
        setSnackbarMessage("_");
        setSnackBarState({sbVertical: 'top', sbHorizontal: 'right', openSb: false});
    };

    /**
     * If search query, role filter or isAdmin changes, reset to first page as the total content will change and thus current page X might not exist anymore (in case of less content).
     */
    useEffect(() => {
        setCurrentPage(1);
        getUsersList();
    }, [searchQuery, roleFilter, companyFilter, isAdmin]);

    /**
     * Fetch users list on page change
     */
    useEffect(() => {
        if (currentPage === 1) return; // Skip if current page is 1 as it is already handled by the other useEffect
        getUsersList();
    }, [currentPage]);

    /**
     * If users list changes, apply processing and list enrichment.
     *
     * roles is a dependency because role_label is read out of it: the effect above sets roles in its own
     * commit, so a pass that ran without it here kept the stale closure and the Role column fell back to the
     * raw stored value — "creator" where the operator should read "Job Owners", "admin" where they should
     * read "Super Admin". It corrected itself on the next fetch, which is why it only ever flickered.
     */
    useEffect(() => {
        const processedData = variantUsersDataProcessorFNMap[variant](rawUsersList, customers)
            .map(row => ({...row, role_label: getLabelForRoleValue(row.role)}));
        setProcessedUsersList(processedData);
    }, [rawUsersList, customers, variant, roles]);

    const handleConfirmationActiveStatusToggle = () => {
        setIsToggleConfirmDialogOpen(false);
        patchUser(selectedUserData?.customer ?? null, selectedUserData.uuid, {is_active: (selectedUserData.is_active !== true)}, isAdmin)
            .then(success => {
                if (success) {
                    openSnackbar("User deactivated successfully");
                    getUsersList();
                } else {
                    openSnackbar("Failed to deactivate the user");
                }
            });
    };

    const handleUserActiveStatusToggle = (row) => {
        setSelectedUserData(row);
        setIsToggleConfirmDialogOpen(true);

    };
    const handleUserEdit = (row) => {
        setSelectedUserData(row);
        setIsCreateEditUserDialogOpen(true);
    };
    const handleUserDelete = (row) => {
        setSelectedUserData(row);
        setIsDeleteConfirmDialogOpen(true);
    };

    const handleConfirmationDeleteUser = () => {
        setIsDeleteConfirmDialogOpen(false);
        deleteUser(selectedUserData?.customer ?? null, selectedUserData.uuid, isAdmin)
            .then(success => {
                if (success) {
                    openSnackbar("User deleted successfully");
                    getUsersList();
                } else {
                    openSnackbar("Failed to delete the user");
                }
            });
    };

    const isPrivilegedOperator = useMemo(() => ["admin", "owner"].includes(currentOperatorRole), [currentOperatorRole]);

    const addCustomRenders = (columnsConfig) => {
        return columnsConfig.map(columnConfig => {
            switch (columnConfig.mnemonic) {
                case "profile_picture":
                    return {...columnConfig, custom_render: renderUserAvatar}
                case "is_active":
                    return {...columnConfig, custom_render: renderUserActiveStatus}
case "actions":
                    return {
                        ...columnConfig,
                        custom_render: (row) => renderUserActions(row, handleUserActiveStatusToggle, handleUserEdit, handleUserDelete, !isPrivilegedOperator)
                    }
                default:
                    return columnConfig;
            }
        });
    };

    // "Failed to add the user" alone left the operator guessing which field the API refused.
    const withReason = (message, error) => {
        const reason = apiErrorReason(error);
        return reason ? `${message}: ${reason}` : message;
    };

    function handleCreateEditUserSubmit(data) {
        setIsCreateEditUserDialogOpen(false);

        const isNewUser = !hasValue(selectedUserData);
        const customerId = isAdmin ? (Object.keys(data).includes("customer") ? data.customer : (selectedUserData?.customer ?? null)) : user?.customer_uuid;

        if (isNewUser) {
            addUser(customerId, data, isAdmin)
                .then(created => {
                    if (created) {
                        openSnackbar(describeInvitation(created));
                        getUsersList();
                    } else {
                        openSnackbar("Failed to add the user");
                    }
                })
                /*
                 * _request re-throws on any status it was not told to accept, so without this a rejected create
                 * left the list unchanged and said nothing at all. It matters more now that the endpoint has a
                 * 404 of its own: a company that cannot be found is refused before the user is written, and an
                 * operator needs to be told that rather than left looking for a row that was never created.
                 */
                .catch(error => {
                    console.error("Adding the user failed.", error);
                    openSnackbar(withReason("Failed to add the user", error));
                });
        } else {
            patchUser(customerId, selectedUserData.uuid, data, isAdmin)
                .then(response => {
                    if (response) {
                        openSnackbar("User edited successfully");
                        getUsersList();
                    } else {
                        openSnackbar("Failed to edit the user");
                    }
                })
                .catch(error => {
                    console.error("Editing the user failed.", error);
                    openSnackbar(withReason("Failed to edit the user", error));
                });
        }

    }

    function renderToggleConfirmDialog() {
        const displayText = selectedUserData?.is_active === true ?
            `You are moving ${selectedUserData?.title} ${selectedUserData.last_name} ${selectedUserData.first_name} (email : ${selectedUserData?.email}) to an "Inactive" state. This means this person will not be able to access their account until it is re-activated by an Admin/Owner.`
            : `You are moving ${selectedUserData?.title} ${selectedUserData.last_name} ${selectedUserData.first_name} (email : ${selectedUserData?.email}) to an "Active" state. `;

        return <ConfirmDialog
            open={isToggleConfirmDialogOpen}
            onClose={() => setIsToggleConfirmDialogOpen(false)}
            onConfirm={handleConfirmationActiveStatusToggle}
            message={displayText}
        />;
    }

    function renderDeleteConfirmDialog() {
        const displayText = `You are about to delete user ${selectedUserData?.title} ${selectedUserData.last_name} ${selectedUserData.first_name} (email : ${selectedUserData?.email}). Please confirm.`;

        return <ConfirmDialog
            open={isDeleteConfirmDialogOpen}
            onClose={() => setIsDeleteConfirmDialogOpen(false)}
            onConfirm={handleConfirmationDeleteUser}
            message={displayText}
        />;
    }

    const [tempSearchQuery, setTempSearchQuery] = useState("");

    /**
     * Debounce the search input to avoid making API calls on every keystroke.
     */
    useEffect(() => {
        const sec = 1000;
        const timerId = setTimeout(() => setSearchQuery(tempSearchQuery), 0.8 * sec);
        return () => clearTimeout(timerId);
    }, [tempSearchQuery, setSearchQuery]);

    const renderSearchFilterAndAdd = () => (
        <Grid
            container
            alignItems="center"
            spacing={2}
            sx={{mt: 4, py: 2}}
        >
            <Grid item xs={3} sx={{display: 'flex', justifyContent: 'flex-start'}}>
                {/* Seach bar */}
                <SearchBar
                    value={tempSearchQuery}
                    onChange={(e) => setTempSearchQuery(e.target.value)}
                    onClear={() => setTempSearchQuery('')}
                />
            </Grid>

            <Grid item xs={isAdmin && customers !== undefined ? 6 : 3}>
                <Grid container alignItems="center" spacing={1}>
                    <Grid item>
                        <LabelledSingleSelect
                            label="Role"
                            options={roles}
                            value={roleFilter}
                            onChange={setRoleFilter}
                            inputProps={{'data-testid': 'role-filter-select'}}
                        />
                    </Grid>
                    {isAdmin && customers !== undefined &&
                        <Grid item>
                            <LabelledSingleSelect
                                label="Company"
                                options={[...customers, {label: "All", value: "all"}]}
                                value={companyFilter}
                                onChange={setCompanyFilter}
                                inputProps={{'data-testid': 'customer-filter-select'}}
                            />
                        </Grid>
                    }
                </Grid>
            </Grid>

            <Grid item xs/>

            <Grid item xs={2} sx={{display: 'flex', justifyContent: 'flex-end'}}>
                <StyledButtonContained
                    type='submit'
                    size="large"
                    fullWidth={true}
                    variant="contained"
                    onClick={() => {
                        setSelectedUserData({});
                        setIsCreateEditUserDialogOpen(true);
                    }}
                    style={{width: "inherit"}}
                    aria-label="add-user-button"
                    aria-disabled={!isPrivilegedOperator}
                    disabled={!isPrivilegedOperator}
                >
                    Add User
                </StyledButtonContained>
            </Grid>
        </Grid>
    );

    /**
     * If admin and variant is AllUsers, fetch all available customers. These will be later used in create/edit user dialog and Company filter field.
     */
    useEffect(() => {
        if (!isAdmin || variant !== "AllUsers") return setCustomers(undefined);

        fetchCustomers()
            .then(data => {
                if (!Array.isArray(data)) throw new Error("Invalid data format for customers received during building user creation/editing override config");
                const availableCustomers = data.map(customer => {
                    return {label: customer.company_name, value: customer.uuid};
                });

                setCustomers([...availableCustomers, {label: "TapeArk", value: "tapeark"}]);
            })
            .catch(error => console.log("Error fetching customers for user creation/editing ", error));
    }, [isAdmin, variant]);

    const overridingConfig = useMemo(() => {
        if (customers === undefined) return undefined;

        const companyConfig = {
            sectionName: "",
            label: "Company",
            valueProperty: "customer",
            type: "multiselect",
            options: {
                source: customers,
                displayKey: "label",
                valueKey: "value",
                safeValuesAlt: [{valueToConvert: null, valueToConvertTo: "tapeark"}],
                xs: 12,
                validationRules: {required: "Company is required"}
            }
        };

        const rolesConfig = dialogBoxConfig.userList.filter(originalFieldConfig => originalFieldConfig.valueProperty === "role")[0];
        rolesConfig.options.source = roles;

        return [
            companyConfig,
            rolesConfig
        ];
    }, [customers]);

    return (
        <Stack direction={'column'} data-testid="user-list-stack">
            <Snackbar
                anchorOrigin={{vertical: sbVertical, horizontal: sbHorizontal}}
                open={openSb}
                onClose={closeSnackbar}
                message={snackbarMessage}
                key={sbVertical + sbHorizontal}
            />
            {renderSearchFilterAndAdd()}
            <DataTable
                data={processedUsersList.length > 0 ? processedUsersList : []}
                columnsConfig={addCustomRenders(variantUsersTableConfigMap[variant])}
                dataContainsFormatting={true}
                showPagination={true}
                entriesCount={userCount}
                currentPage={currentPage}
                totalPages={totalPages}
                handlePageChangeCallback={handlePageChange}
            />
            <CreateEditDialog
                isDialogOpen={isCreateEditUserDialogOpen}
                handleClose={() => setIsCreateEditUserDialogOpen(false)}
                title={hasValue(selectedUserData) ? "Edit User" : "Add New User"}
                variant={"userList"}
                data={selectedUserData}
                overridingConfig={overridingConfig}
                onSubmitFn={handleCreateEditUserSubmit}
            />
            {renderToggleConfirmDialog()}
            {renderDeleteConfirmDialog()}
        </Stack>
    );
};

